import React, { ReactElement } from 'react'
import { withRouter, RouteComponentProps, useHistory } from 'react-router-dom'
import { MetaTitle, LoadError } from '../../Components/UI'
import { Typography, Alert } from 'antd'
import { EnquiryDetails } from './EnquiryDetails'
import { ProposalCard } from './ProposalCard'
import {
  AllProposalsProposalFragment,
  useAllProposalsEventQuery,
} from './AllProposals.generated-types'
import { Suppliers } from '../../Components/Suppliers'
import { s, SUPPLIER_DEALS_CONTAINER_DIV } from './styles'
import { ROUTES } from '../../Constants'
import { ProposalStatus } from '../../types'

const UUID_REGULAR_EXPRESSION: RegExp =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const { Title, Text } = Typography

const buildProposalCards = (
  proposals: AllProposalsProposalFragment[] | null
): JSX.Element[] => {
  if (!proposals || !proposals.length) {
    return []
  }

  return proposals
    .filter(
      (proposal) =>
        proposal.status === ProposalStatus.SentToGuest
    )
    .map((proposal) => (
      <ProposalCard key={proposal.UUID} proposal={proposal} />
    ))
}

const AllProposals: React.FC<RouteComponentProps<{ eventUUID: string }>> = ({
  match: {
    params: { eventUUID: eventUUIDToTest },
  },
}) => {
  const history = useHistory()

  const validEventUUID = eventUUIDToTest.match(UUID_REGULAR_EXPRESSION)

  const queryVariables = validEventUUID
    ? {
      // The valid UUID will always be the 0th element so this is safe
      UUID: validEventUUID[0],
    }
    : null

  const [{ fetching, error, data }] = useAllProposalsEventQuery({
    variables: queryVariables,
    pause: !queryVariables,
  })

  if (!validEventUUID) {
    history.push(ROUTES.EVENT_HUB)
    return null
  }

  if (error || fetching) return <LoadError load={fetching} error={error} />

  const event = data?.getEventByUUID

  const proposalCards = buildProposalCards(event?.proposals)

  const hasSupplierDeals =
    event?.supplierDeals && event.supplierDeals.length > 0

  const ProposalsGallery = (): ReactElement => (
    <section style={s.gallery}>
      {proposalCards.length ? (
        proposalCards
      ) : (
        <Alert
          style={{ width: '100%', margin: 8 }}
          showIcon
          type={'info'}
          message="There are no proposals for this event"
        />
      )}
    </section>
  )

  const Wrapper = ({ meta, children }): ReactElement => (
    <section
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexDirection: 'column',
        marginTop: '40px',
      }}
    >
      <MetaTitle title={meta} />
      <Title style={s.title} level={2}>
        All proposals from venues
      </Title>
      <Text style={s.subhead}>
        Explore offers from venues and book the one that works best for you
      </Text>
      <EnquiryDetails event={event} />
      {children}
      {hasSupplierDeals ? (
        <div style={SUPPLIER_DEALS_CONTAINER_DIV}>
          <Suppliers supplierDeals={event.supplierDeals} />
        </div>
      ) : null}
    </section>
  )

  return (
    <Wrapper meta={'Proposals - HeadBox'}>
      <ProposalsGallery />
    </Wrapper>
  )
}

export default withRouter(AllProposals)
